import React from 'react';
import { Calendar, ChevronRight } from 'lucide-react';
import { Intervention, News, ContentType, CombinedItem } from '../../../types/contentTypes';
import { getInterventionCategoryLabel, getNewsCategoryLabel } from '../../../utils/categoryHelpers';

interface InterventionListProps {
  contentType: ContentType;
  filteredInterventions: Intervention[];
  filteredNews: News[];
  allItems: CombinedItem[];
  onItemSelect: (id: string) => void;
}

const InterventionList: React.FC<InterventionListProps> = ({
  contentType,
  filteredInterventions,
  filteredNews,
  allItems,
  onItemSelect
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {/* Display interventions */}
      {contentType === 'interventions' && filteredInterventions.map(intervention => (
        <div 
          key={intervention.id}
          className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => onItemSelect(intervention.id)}
        >
          <div className="h-48 overflow-hidden">
            <img 
              src={intervention.image} 
              alt={intervention.title} 
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" 
            />
          </div>
          <div className="p-4">
            <div className="flex flex-wrap gap-2 mb-3">
              <span className="bg-primary/10 text-primary text-xs font-medium px-2 py-1 rounded-full">
                {getInterventionCategoryLabel(intervention.category)}
              </span>
              <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full flex items-center">
                <Calendar className="h-3 w-3 mr-1" />
                {new Date(intervention.date).toLocaleDateString('sl-SI')}
              </span>
            </div>
            <h3 className="text-lg font-semibold mb-2">{intervention.title}</h3>
            <p className="text-gray-600 text-sm mb-4 line-clamp-2">{intervention.excerpt}</p>
            <button className="text-primary hover:text-primary-700 font-medium flex items-center text-sm">
              Preberi več
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      ))}

      {/* Display news */}
      {contentType === 'news' && filteredNews.map(newsItem => (
        <div 
          key={newsItem.id}
          className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => onItemSelect(newsItem.id)}
        >
          <div className="h-48 overflow-hidden">
            <img 
              src={newsItem.image} 
              alt={newsItem.title} 
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" 
            />
          </div>
          <div className="p-4">
            <div className="flex flex-wrap gap-2 mb-3">
              <span className="bg-primary/10 text-primary text-xs font-medium px-2 py-1 rounded-full">
                {getNewsCategoryLabel(newsItem.category)}
              </span>
              <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full flex items-center">
                <Calendar className="h-3 w-3 mr-1" />
                {new Date(newsItem.date).toLocaleDateString('sl-SI')}
              </span>
            </div>
            <h3 className="text-lg font-semibold mb-2">{newsItem.title}</h3>
            <p className="text-gray-600 text-sm mb-4 line-clamp-2">{newsItem.excerpt}</p>
            <button className="text-primary hover:text-primary-700 font-medium flex items-center text-sm">
              Preberi več
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      ))}

      {/* Display combined list */}
      {contentType === 'all' && allItems.map(item => (
        <div 
          key={item.id}
          className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => onItemSelect(item.id)}
        >
          <div className="h-48 overflow-hidden">
            <img 
              src={item.image} 
              alt={item.title} 
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" 
            />
          </div>
          <div className="p-4">
            <div className="flex flex-wrap gap-2 mb-3">
              <span className="bg-primary/10 text-primary text-xs font-medium px-2 py-1 rounded-full">
                {item.type === 'intervention' 
                  ? getInterventionCategoryLabel(item.category) 
                  : getNewsCategoryLabel(item.category)}
              </span>
              <span className="bg-secondary/10 text-secondary text-xs font-medium px-2 py-1 rounded-full">
                {item.type === 'intervention' ? 'Intervencija' : 'Novica'}
              </span>
              <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full flex items-center">
                <Calendar className="h-3 w-3 mr-1" />
                {new Date(item.date).toLocaleDateString('sl-SI')}
              </span>
            </div>
            <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
            <p className="text-gray-600 text-sm mb-4 line-clamp-2">{item.excerpt}</p>
            <button className="text-primary hover:text-primary-700 font-medium flex items-center text-sm">
              Preberi več
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      ))}

      {/* No results message */}
      {contentType === 'interventions' && filteredInterventions.length === 0 && (
        <div className="col-span-full text-center py-8">
          <p className="text-gray-500 text-lg">Ni najdenih intervencij.</p>
        </div>
      )}

      {contentType === 'news' && filteredNews.length === 0 && (
        <div className="col-span-full text-center py-8">
          <p className="text-gray-500 text-lg">Ni najdenih novic.</p>
        </div>
      )}

      {contentType === 'all' && allItems.length === 0 && (
        <div className="col-span-full text-center py-8">
          <p className="text-gray-500 text-lg">Ni najdenih rezultatov.</p>
        </div>
      )}
    </div>
  );
};

export default InterventionList;